'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { tmdb } from '@/lib/tmdb';
import { rankCandidates } from '@/lib/searchRanking';
import { useStore } from '@/lib/store';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Film, Tv, Star, Calendar, Loader2, Plus, Check, User, Clock, X as XIcon } from 'lucide-react';
import Image from 'next/image';
import { format } from 'date-fns';

interface MediaSearchResult {
  id: number;
  title?: string;
  name?: string;
  poster_path: string | null;
  profile_path?: string | null;
  backdrop_path: string | null;
  vote_average?: number;
  overview?: string;
  release_date?: string;
  first_air_date?: string;
  known_for_department?: string;
  media_type: 'tv' | 'movie' | 'person';
}

interface UserSearchResult {
  uid: string;
  username: string;
  displayName?: string;
  photoURL?: string;
  media_type: 'user';
}

type SearchResult = MediaSearchResult | UserSearchResult;

interface SearchDropdownProps {
  placeholder?: string;
  className?: string;
  onSelect?: (result: SearchResult) => void;
}

export function SearchDropdown({
  placeholder = "Search movies, TV shows, people, and users...",
  className = "",
  onSelect
}: SearchDropdownProps) {
  const router = useRouter();
  const { user } = useAuth();
  const { watchlist, addToWatchlist } = useStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [addingId, setAddingId] = useState<number | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  // Persisted recent searches (last 6 unique queries).
  const RECENT_KEY = 'epify-recent-searches';
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) setRecentSearches(JSON.parse(raw).slice(0, 6));
    } catch { /* ignore */ }
  }, []);
  const rememberSearch = useCallback((q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setRecentSearches((prev) => {
      const next = [trimmed, ...prev.filter((p) => p.toLowerCase() !== trimmed.toLowerCase())].slice(0, 6);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ }
  }, []);

  const searchTimeout = useRef<NodeJS.Timeout>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const searchContent = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      // Pull TWO pages of multi-search + dedicated person/show/movie searches +
      // our user search, all in parallel. The wider candidate pool gives the
      // ranker (lib/searchRanking.ts) something to actually pick from, instead
      // of the literal top-4 TMDB happens to return for the verbatim query.
      const safe = <T,>(p: Promise<T>, fallback: T): Promise<T> => p.catch(() => fallback);
      const empty = { results: [] as any[] };
      const [m1, m2, peopleData, showsData, moviesData, usersResponse] = await Promise.all([
        safe(tmdb.searchMulti(searchQuery, 1), empty),
        safe(tmdb.searchMulti(searchQuery, 2), empty),
        safe(tmdb.searchPeople(searchQuery, 1), empty),
        safe(tmdb.searchShows(searchQuery, 1), empty),
        safe(tmdb.searchMovies(searchQuery, 1), empty),
        safe(
          fetch(`/api/users/search?q=${encodeURIComponent(searchQuery)}`).then((res) => res.json()),
          { users: [] as any[] }
        ),
      ]);

      // Build the candidate pool (dedupe by media_type:id) and require a poster
      // or profile photo so the row renders correctly.
      const seen = new Set<string>();
      const candidates: any[] = [];
      const push = (item: any, type: 'movie' | 'tv' | 'person') => {
        const key = `${type}:${item.id}`;
        if (seen.has(key)) return;
        if (type === 'person' ? !item.profile_path : !item.poster_path) return;
        seen.add(key);
        candidates.push({ ...item, media_type: type });
      };
      // Multi-search (carries media_type)
      for (const r of [...m1.results, ...m2.results]) {
        if (r?.media_type === 'movie' || r?.media_type === 'tv' || r?.media_type === 'person') {
          push(r, r.media_type);
        }
      }
      // Dedicated per-type searches (more reliable than multi for niche queries)
      for (const r of peopleData.results) push(r, 'person');
      for (const r of showsData.results) push(r, 'tv');
      for (const r of moviesData.results) push(r, 'movie');

      // Split, rank within each kind, then take more than the old hard cap.
      const peopleAll = candidates.filter((c) => c.media_type === 'person');
      const mediaAll = candidates.filter((c) => c.media_type !== 'person');

      const rankedMedia = rankCandidates(mediaAll, searchQuery).slice(0, 8);
      const rankedPeople = rankCandidates(peopleAll, searchQuery).slice(0, 4);

      const mediaResults: SearchResult[] = [...rankedPeople, ...rankedMedia];

      // Users — small, stays at the top
      const userResults: SearchResult[] = (usersResponse.users || [])
        .slice(0, 3)
        .map((user: any) => ({
          uid: user.uid,
          username: user.username,
          displayName: user.displayName,
          photoURL: user.photoURL,
          media_type: 'user' as const,
        }));

      const combinedResults = [...userResults, ...mediaResults];

      setResults(combinedResults);
      setShowDropdown(combinedResults.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      setResults([]);
      setShowDropdown(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout for search
    if (value.trim()) {
      searchTimeout.current = setTimeout(() => {
        searchContent(value);
      }, 200); // 200ms debounce — responsive without over-firing
    } else {
      setResults([]);
      setShowDropdown(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    // Remember the active query before clearing it (helps recent-searches).
    if (query.trim()) rememberSearch(query);

    setQuery('');
    setResults([]);
    setShowDropdown(false);
    setSelectedIndex(-1);

    if (onSelect) {
      onSelect(result as any);
    }

    // Navigate based on result type
    if (result.media_type === 'user') {
      router.push(`/profile/${result.username}`);
    } else if (result.media_type === 'person') {
      router.push(`/person/${(result as MediaSearchResult).id}`);
    } else {
      router.push(`/${result.media_type}/${(result as MediaSearchResult).id}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || results.length === 0) {
      if (e.key === 'Enter' && query.trim()) {
        // Navigate to search results page
        rememberSearch(query);
        router.push(`/search?q=${encodeURIComponent(query.trim())}`);
        setShowDropdown(false);
        setQuery('');
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleResultClick(results[selectedIndex]);
        } else if (query.trim()) {
          // Navigate to search results page
          rememberSearch(query);
          router.push(`/search?q=${encodeURIComponent(query.trim())}`);
          setShowDropdown(false);
          setQuery('');
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const getDisplayTitle = (result: SearchResult) => {
    if (result.media_type === 'user') {
      return result.displayName || result.username;
    }
    const mediaResult = result as MediaSearchResult;
    return mediaResult.title || mediaResult.name || 'Unknown Title';
  };

  const getDisplayDate = (result: SearchResult) => {
    if (result.media_type === 'user' || result.media_type === 'person') return null;
    const mediaResult = result as MediaSearchResult;
    const date = mediaResult.release_date || mediaResult.first_air_date;
    return date ? format(new Date(date), 'yyyy') : null;
  };

  const isInWatchlist = (id: number) => {
    return watchlist.some(item => item.id === id);
  };

  const handleAddToWatchlist = async (e: React.MouseEvent, item: SearchResult) => {
    e.stopPropagation();

    if (!user) {
      router.push('/auth/signin');
      return;
    }

    // Only media can be added to watchlist
    if (item.media_type === 'user' || item.media_type === 'person') {
      return;
    }

    const mediaItem = item as MediaSearchResult;

    if (isInWatchlist(mediaItem.id)) {
      return;
    }

    setAddingId(mediaItem.id);

    try {
      const type = mediaItem.media_type || (mediaItem.first_air_date ? 'tv' : 'movie');

      if (type === 'tv') {
        // Import ContentChecker dynamically for TV shows
        const { ContentChecker } = await import('@/lib/contentChecker');

        // Fetch full show details and calculate update schedule
        const updateDetails = await ContentChecker.checkSingleShow(mediaItem.id, 0);

        const watchlistItem = {
          id: mediaItem.id,
          type: 'tv' as const,
          name: mediaItem.name || mediaItem.title || '',
          posterPath: mediaItem.poster_path,
          backdropPath: mediaItem.backdrop_path,
          overview: mediaItem.overview || '',
          watchedSeasons: [],
          lastChecked: new Date().toISOString(),
          hasNewContent: updateDetails.hasNewSeason || updateDetails.hasNewEpisodes,
          totalSeasons: updateDetails.totalSeasons || 0,
          addedAt: new Date().toISOString(),
          rating: mediaItem.vote_average || 0,
          genres: [],
          // Intelligent update scheduling
          status: updateDetails.status,
          nextCheckDate: updateDetails.nextCheckDate,
          updateFrequency: updateDetails.updateFrequency,
          // Content details populated immediately
          newSeasonNumber: updateDetails.hasNewSeason ? updateDetails.newSeasonNumber : undefined,
          nextEpisode: updateDetails.nextEpisode,
        };

        await addToWatchlist(watchlistItem);
      } else {
        // Movies - use simple logic
        const watchlistItem = {
          id: mediaItem.id,
          type: 'movie' as const,
          name: mediaItem.name || mediaItem.title || '',
          posterPath: mediaItem.poster_path,
          backdropPath: mediaItem.backdrop_path,
          overview: mediaItem.overview || '',
          lastChecked: new Date().toISOString(),
          hasNewContent: false,
          addedAt: new Date().toISOString(),
          rating: mediaItem.vote_average || 0,
          genres: []
        };

        await addToWatchlist(watchlistItem);
      }
    } catch (error) {
      // Failed to add to watchlist
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div ref={dropdownRef} className="relative w-full">
      <div className="relative group">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-white z-10 pointer-events-none transition-colors" strokeWidth={2.5} />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // Reveal either existing results or the recent-searches panel.
            if (results.length > 0 || (!query.trim() && recentSearches.length > 0)) {
              setShowDropdown(true);
            }
          }}
          className={`w-full h-11 rounded-full bg-white/10 ring-1 ring-white/15 border-0 pl-11 pr-10 text-white placeholder:text-gray-400 backdrop-blur-md focus-visible:ring-2 focus-visible:ring-red-500/60 focus-visible:bg-white/15 transition-all ${className}`}
        />
        {loading && (
          <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Recent searches — shown when the input is focused but empty */}
      {showDropdown && results.length === 0 && !query.trim() && recentSearches.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-2 bg-black/90 ring-1 ring-white/10 rounded-2xl shadow-[0_0_40px_-10px_rgba(220,38,38,0.4)] backdrop-blur-xl overflow-hidden p-1.5">
          <div className="flex items-center justify-between px-3 pt-2 pb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Recent searches</span>
            <button
              onClick={clearRecentSearches}
              className="text-[11px] text-gray-400 hover:text-white transition-colors"
            >
              Clear
            </button>
          </div>
          {recentSearches.map((q) => (
            <button
              key={q}
              type="button"
              onMouseDown={(e) => e.preventDefault() /* keep input focused */}
              onClick={() => {
                setQuery(q);
                searchContent(q);
                inputRef.current?.focus();
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              <Clock className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span className="flex-1 text-left truncate">{q}</span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setRecentSearches((prev) => {
                    const next = prev.filter((p) => p !== q);
                    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
                    return next;
                  });
                }}
                className="p-1 -mr-1 rounded-md text-gray-500 hover:text-white hover:bg-white/10"
                aria-label={`Remove ${q} from recent searches`}
              >
                <XIcon className="h-3.5 w-3.5" />
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Dropdown */}
      {showDropdown && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-2 bg-black/90 ring-1 ring-white/10 rounded-2xl shadow-[0_0_40px_-10px_rgba(220,38,38,0.4)] backdrop-blur-xl max-h-[28rem] overflow-y-auto overflow-hidden p-1.5">
          {results.map((result, index) => {
            const title = getDisplayTitle(result);
            const year = getDisplayDate(result);
            const isSelected = index === selectedIndex;

            const inWatchlist = isInWatchlist(result.id);
            const isAdding = addingId === result.id;

            // Determine if this is a user or media result
            const isUser = result.media_type === 'user';
            const isPerson = result.media_type === 'person';
            const isMedia = result.media_type === 'movie' || result.media_type === 'tv';

            const mediaResult = result as MediaSearchResult;
            const userResult = result as UserSearchResult;

            // Get image source
            let imageSrc: string | null = null;
            if (isUser) {
              imageSrc = userResult.photoURL || null;
            } else if (isPerson) {
              imageSrc = mediaResult.profile_path
                ? tmdb.getImageUrl(mediaResult.profile_path, 'w200')
                : null;
            } else {
              imageSrc = mediaResult.poster_path
                ? tmdb.getImageUrl(mediaResult.poster_path, 'w200')
                : null;
            }

            return (
              <div
                key={isUser ? `user-${userResult.uid}` : `${mediaResult.id}-${result.media_type}`}
                className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-colors ${
                  isSelected ? 'bg-red-600/20' : 'hover:bg-white/5'
                }`}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <button
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  onClick={() => handleResultClick(result)}
                >
                  {/* Image/Avatar */}
                  <div className={`flex-shrink-0 bg-gray-800 rounded overflow-hidden ${
                    isUser || isPerson ? 'w-12 h-12 rounded-full' : 'w-12 h-16'
                  }`}>
                    {imageSrc ? (
                      <Image
                        src={imageSrc}
                        alt={title}
                        width={48}
                        height={isUser || isPerson ? 48 : 64}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800">
                        {isUser ? (
                          <User className="h-6 w-6 text-gray-600" />
                        ) : isPerson ? (
                          <User className="h-6 w-6 text-gray-600" />
                        ) : result.media_type === 'tv' ? (
                          <Tv className="h-6 w-6 text-gray-600" />
                        ) : (
                          <Film className="h-6 w-6 text-gray-600" />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate text-sm">
                          {title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-1">
                            {isUser ? (
                              <>
                                <User className="h-3 w-3 text-purple-500" />
                                <span className="text-xs text-gray-400">
                                  User
                                </span>
                              </>
                            ) : isPerson ? (
                              <>
                                <User className="h-3 w-3 text-orange-500" />
                                <span className="text-xs text-gray-400">
                                  {mediaResult.known_for_department || 'Person'}
                                </span>
                              </>
                            ) : result.media_type === 'tv' ? (
                              <>
                                <Tv className="h-3 w-3 text-green-500" />
                                <span className="text-xs text-gray-400 capitalize">
                                  TV Show
                                </span>
                              </>
                            ) : (
                              <>
                                <Film className="h-3 w-3 text-blue-500" />
                                <span className="text-xs text-gray-400 capitalize">
                                  Movie
                                </span>
                              </>
                            )}
                          </div>
                          {year && (
                            <>
                              <span className="text-xs text-gray-500">•</span>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3 text-gray-500" />
                                <span className="text-xs text-gray-400">{year}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      {!isUser && mediaResult.vote_average && mediaResult.vote_average > 0 && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                          <span className="text-xs text-gray-400">
                            {mediaResult.vote_average.toFixed(1)}
                          </span>
                        </div>
                      )}
                    </div>
                    {!isUser && mediaResult.overview && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {mediaResult.overview.slice(0, 120)}...
                      </p>
                    )}
                    {isUser && userResult.username && (
                      <p className="text-xs text-gray-500 mt-1">
                        @{userResult.username}
                      </p>
                    )}
                  </div>
                </button>

                {/* Add to Watchlist Button - Only for movies and TV shows */}
                {user && isMedia && (
                  <Button
                    size="sm"
                    variant={inWatchlist ? 'secondary' : 'default'}
                    className={`flex-shrink-0 h-8 px-3 ${
                      inWatchlist
                        ? 'bg-green-600/20 text-green-400 border-green-600/50 hover:bg-green-600/30'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                    onClick={(e) => handleAddToWatchlist(e, result)}
                    disabled={inWatchlist || isAdding}
                  >
                    {isAdding ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : inWatchlist ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <Plus className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </div>
            );
          })}

          {/* Show more results link */}
          <div className="mt-1 pt-1.5 border-t border-white/10">
            <button
              className="w-full text-center text-sm text-red-400 hover:text-white hover:bg-red-600/20 rounded-lg py-2.5 transition-colors"
              onClick={() => {
                rememberSearch(query);
                router.push(`/search?q=${encodeURIComponent(query.trim())}`);
                setShowDropdown(false);
                setQuery('');
              }}
            >
              View all results for &ldquo;{query}&rdquo;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}